import { useId } from 'react';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import type { ReactNode } from 'react';

interface AccordionItemProps {
  question: string;
  answer: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** See `FaqAccordion`'s prop of the same name — scales this card down to the
   *  app landing's mobile FAQ design (Figma node 5663:15427) instead of using
   *  the desktop metrics (5210:11418) at every width. */
  mobileFluid?: boolean;
}

// Fluid values scale linearly with viewport width between the two reference
// mockups — 402px (5663:15427) and 1440px (5210:11418) — via the standard
// `clamp(min, intercept + slope*vw, max)` formula.
const FLUID_CARD_RADIUS = 'rounded-[clamp(14px,11.68px+0.578vw,20px)]';
const FLUID_CARD_PX = 'px-[clamp(16px,12.9px+0.771vw,24px)]';
const FLUID_QUESTION_SIZE = 'text-[clamp(16px,14.45px+0.385vw,20px)]';
const FLUID_TOGGLE_SIZE = 'size-[clamp(24px,16.25px+1.927vw,44px)]';
const FLUID_ICON_SIZE = 'size-[clamp(16px,12.9px+0.771vw,24px)]';
const FLUID_ANSWER_SIZE = 'text-[clamp(14px,13.23px+0.193vw,16px)]';
const FLUID_ANSWER_LEADING = 'leading-[clamp(20px,18.45px+0.385vw,24px)]';

export function AccordionItem({
  question,
  answer,
  open,
  onToggle,
  mobileFluid = false,
}: AccordionItemProps) {
  const contentId = useId();

  return (
    <div className={`bg-surface ${mobileFluid ? FLUID_CARD_RADIUS : 'rounded-card'}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={contentId}
        className={`flex w-full cursor-pointer items-start justify-between gap-6 py-4 text-left sm:items-center ${
          mobileFluid ? FLUID_CARD_PX : 'px-6'
        }`}
      >
        <span
          className={`text-ink font-medium tracking-tight ${mobileFluid ? FLUID_QUESTION_SIZE : 'text-xl'}`}
        >
          {question}
        </span>
        <span
          className={`bg-surface-dark relative flex shrink-0 items-center justify-center rounded-full text-white ${
            mobileFluid ? FLUID_TOGGLE_SIZE : 'size-11'
          }`}
        >
          <IconPlus
            aria-hidden
            className={`absolute transition-all duration-200 ${mobileFluid ? FLUID_ICON_SIZE : 'size-6'} ${
              open ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
            }`}
          />
          <IconMinus
            aria-hidden
            className={`absolute transition-all duration-200 ${mobileFluid ? FLUID_ICON_SIZE : 'size-6'} ${
              open ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
            }`}
          />
        </span>
      </button>
      <div
        id={contentId}
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        {/* The design models question + answer as one flex column with a 16px
            gap inside the card's own 16px vertical padding (Figma node
            5210:11418). The button's `py-4` already supplies the 16px gap above
            the answer, so all that's needed here is the matching 16px below —
            `pb-2` left the text almost touching the card's bottom edge.

            Answer colour is a genuine breakpoint-specific difference, not a
            token mix-up: mobile wants `text/tertiary` (#6a667f, Figma
            5663:15427) while desktop wants `text/secondary` (#8a869c,
            5210:11418, matching this component's original colour) — confirmed
            against both mockups rather than assumed. Colour can't share the
            `vw`-based clamp() used for sizes above (calc() has no notion of
            interpolating between two colours), so this stays a plain
            breakpoint swap. */}
        <div className="overflow-hidden">
          <p
            className={`pb-4 ${mobileFluid ? FLUID_CARD_PX : 'px-6'} ${
              mobileFluid
                ? `${FLUID_ANSWER_SIZE} ${FLUID_ANSWER_LEADING} text-text-tertiary sm:text-text-secondary`
                : 'text-text-secondary text-base'
            }`}
          >
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}
